// ✅ Static metadata export (used in project listing page)
export const metadata = {
  title: 'Modern Spanish Style Kitchen Remodeling in Corona, CA 92883',
  description:
    'A modern Spanish style kitchen remodeling project in Corona, CA featuring custom wood cabinetry, stone countertops, arched details, and refined craftsmanship by Modern Build.',
  link: '/services/kitchen-remodeling',
  height: 'h-[467px]',
  image:
    '/projects/modern-spanish-style-kitchen-remodeling-in-corona-92883/modern-spanish-style-kitchen-remodeling-in-corona-92883.webp',
};

import fs from 'fs';
import path from 'path';
import Head from 'next/head';
import Header from '../../../components/Header';
import Hero from '../../../components/Hero';
import ContentBlock from '../../../components/ContentBlock';
import { VideoSection } from '../../../components/Videos';
import Gallery from '../../../components/Gallery';
import SocialProof from '../../../components/SocialProof';
import ContactUs from '../../../components/ContactUs';
import Footer from '../../../components/Footer';
import { generateWebPageSchema } from '../../../utils/generateWebPageSchema';

export async function getStaticProps() {
  const slug = 'modern-spanish-style-kitchen-remodeling-in-corona-92883';
  const galleryPath = path.join(
    process.cwd(),
    'public',
    'projects',
    slug,
    'gallery'
  );

  let galleryImages = [];

  // ✅ Load gallery images
  if (fs.existsSync(galleryPath)) {
    const files = fs.readdirSync(galleryPath).filter(file =>
      /\.(jpg|jpeg|png|webp)$/i.test(file)
    );

    galleryImages = files.map(file => ({
      src: `/projects/${slug}/gallery/${file}`,
      alt: file
        .replace(/\.(jpg|jpeg|png|webp)$/i, '')
        .replace(/[-_]/g, ' '),
      title: file
        .replace(/\.(jpg|jpeg|png|webp)$/i, '')
        .replace(/[-_]/g, ' '),
    }));
  }

  return {
    props: {
      galleryImages,
    },
  };
}

export default function ModernSpanishKitchenRemodelingCorona({
  galleryImages,
}) {
  const meta = metadata;

  return (
    <>
      <Head>
        <title>{meta.title}</title>
        <meta name="title" content={meta.title}/>
        <meta name="description" content={meta.description} />
        <meta name="author" content="Modern Build Home Remodeling" />
        <meta
          name="keywords"
          content="modern Spanish kitchen remodeling, Corona CA 92883 kitchen remodel, Spanish style kitchen, custom kitchen remodeling, Modern Build"
        />
        <link
          rel="canonical"
          href="https://modernbuildremodel.com/projects/modern-spanish-style-kitchen-remodeling-in-corona-92883"
        />

        {/* Open Graph */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta
          property="og:image"
          content={`https://modernbuildremodel.com${meta.image}`}
        />
        <meta
          property="og:url"
          content="https://modernbuildremodel.com/projects/modern-spanish-style-kitchen-remodeling-in-corona-92883"
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:site_name"
          content="Modern Build Home Remodeling"
        />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={meta.title}
        />
        <meta
          name="twitter:description"
          content={meta.description}
        />
        <meta
          name="twitter:image"
          content={`https://modernbuildremodel.com${meta.image}`}
        />
        <meta name="twitter:site" content="@modernbuildhome" />

        {/* Breadcrumb Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: 'Home',
                  item: 'https://modernbuildremodel.com',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: 'Projects',
                  item: 'https://modernbuildremodel.com/projects',
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name:
                    'Modern Spanish Style Kitchen Remodeling in Corona CA 92883',
                  item:
                    'https://modernbuildremodel.com/projects/modern-spanish-style-kitchen-remodeling-in-corona-92883',
                },
              ],
            }),
          }}
        />

        {/* WebPage Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title:
                  'Modern Spanish Style Kitchen Remodeling in Corona, CA 92883 | Modern Build',
                description:
                  'Explore a modern Spanish style kitchen remodeling project in Corona, CA featuring custom cabinetry, stone surfaces, and refined architectural details.',
                slug:
                  '/projects/modern-spanish-style-kitchen-remodeling-in-corona-92883',
              })
            ),
          }}
        />
      </Head>

      <Header />

      <Hero
        category="modernSpanishKitchenRemodelingInCoronaCA92883"
        page="page"
        hasContentH1={false}
      />

      <ContentBlock
        subtitle="PROJECT OVERVIEW"
        title="Modern Spanish Style Kitchen Remodeling in Corona"
        description="This kitchen remodeling project in Corona, CA focused on blending modern functionality with classic Spanish architectural character. The renovation included custom wood cabinetry, stone countertops, a waterfall island, upgraded lighting, and carefully preserved design details that bring warmth and balance to the space."
        extraText="The goal was to modernize the kitchen while respecting the home’s original style. Every design choice was made to improve flow, storage, and everyday usability while maintaining a timeless look."
        buttonText="Plan My Kitchen Remodel"
        buttonLink="/services/kitchen-remodeling"
      />

      <Gallery images={galleryImages} />

      <section className="bg-white py-[120px]">
        <div className="container mx-auto px-6">
          <VideoSection
            videoUrl="https://www.youtube.com/embed/9KBpesY-SBA"
            serviceType="Kitchen Remodeling"
            location="Corona, CA"
            description="Watch how this outdated kitchen was transformed into a modern Spanish style space that balances craftsmanship, warmth, and everyday functionality."
            serviceLink="/services/kitchen-remodeling"
            coverImage={meta.image}
            isVideoLeft={true}
          />
        </div>
      </section>


     <section className="bg-white py-[120px]">
  <div className="container mx-auto px-6 max-w-4xl">

    <h2 className="text-2xl font-light text-primary leading-tight font-gilda mb-4">
      Modern Spanish Style Kitchen Remodeling in Corona, CA 92883
    </h2>

    <p className="text-gray font-light text-lg leading-relaxed">
      This modern Spanish style kitchen remodeling project in Corona, CA 92883 showcases how thoughtful design and skilled craftsmanship can completely redefine a home. The homeowners wanted a kitchen that felt updated and functional while still honoring the architectural character of their Spanish style residence. If you’re looking for a <a
        href="https://modernbuildremodel.com/kitchen-remodeling/california/corona/92883"
        className="text-primary underline"
      >
        kitchen remodeling in Corona, CA 92883
      </a>, this project is a perfect example of what our team can deliver.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      At Modern Build Home Remodeling, we specialize in creating spaces that balance beauty, durability, and real-world usability. This project demonstrates how those principles come together to transform both the aesthetic and functional aspects of the kitchen.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Project Overview
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      The original kitchen featured dated cabinetry, limited lighting, and a layout that restricted movement and storage. While the bones of the space were solid, the finishes and configuration no longer supported the homeowners’ needs. By choosing <a
        href="https://modernbuildremodel.com/kitchen-remodeling/california/corona/92883"
        className="text-primary underline"
      >
        kitchen remodeling in Corona, CA 92883
      </a>, the homeowners were able to achieve a modern, functional, and timeless space.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      Our objective was to modernize the kitchen without stripping away its identity. Spanish style homes are known for warmth, texture, and timeless appeal, and preserving those qualities was essential.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Design Strategy
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      We began with a complete redesign of the layout to improve flow and usability. A large central island was introduced to serve as a functional workspace, casual dining area, and visual anchor. The waterfall stone countertop adds a modern touch while maintaining a natural, grounded feel.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      Custom wood cabinetry was selected to enhance warmth and provide ample storage. The cabinet design balances clean lines with subtle detailing to reflect both modern and traditional influences.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      Architectural elements such as arched openings and exposed wood beams were incorporated to reinforce the Spanish character of the home. Updated lighting fixtures were installed to improve visibility and highlight key design features.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Craftsmanship and Execution
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      Execution is where design becomes reality. Our experienced team managed every phase of the remodeling process, from demolition to final finishes. Attention to detail was critical, especially when integrating modern elements into an existing architectural style.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      We prioritize quality materials, precise installation, and long-term durability. This ensures the kitchen not only looks beautiful but continues to perform for years to come. If you are planning your own <a
        href="https://modernbuildremodel.com/kitchen-remodeling/california/corona/92883"
        className="text-primary underline"
      >
        kitchen remodeling in Corona, CA 92883
      </a>, we can guide you through the process from concept to completion.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Results and Homeowner Experience
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      The completed kitchen now feels open, inviting, and functional. Improved lighting, better storage, and a refined layout have transformed how the homeowners use and enjoy the space. The modern Spanish aesthetic creates a timeless look that will age gracefully rather than follow short-lived trends.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      This project also highlights the value of working with a remodeling contractor who understands both design and construction. Our approach ensures that aesthetics and functionality work together seamlessly.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      For homeowners exploring broader renovation options, we also offer comprehensive{' '}
      <a
        href="https://modernbuildremodel.com/services/complete-home-remodeling"
        className="text-primary underline"
      >
        home remodeling services
      </a>{' '}
      across Southern California.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Serving <a
        href="https://modernbuildremodel.com/kitchen-remodeling/california/corona/92883"
      >Corona, CA 92883</a> and Beyond
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      We proudly serve Corona, Riverside County, and surrounding Southern California communities. Every project we take on is approached with care, transparency, and a commitment to quality. If you’re planning a kitchen remodel or home renovation and want results that feel intentional and lasting, Modern Build Home Remodeling is here to help.
    </p>

  </div>
</section>


      <SocialProof />
      <ContactUs />
      <Footer />
    </>
  );
}
